import { useCallback, useState, useRef } from 'react'
import { createFileRoute, useRouter } from '@tanstack/react-router'
import { FullView, FullViewContent } from '../../../../components/Modal'
import { ActionButton } from '../../../../components/Button'
import { Check, ChevronBackward } from '@nine-thirty-five/material-symbols-react/rounded'
import { type ProjectFormData, useCreateProject } from '@zendia/frontend-shared'
import { axiosRequest } from '../../../../utilities/axiosClient'
import { ProjectForm, type ProjectFormRef } from '../../../../components/Projects/ProjectForm'

export const Route = createFileRoute('/_authenticated/projects/new/form')({
  component: RouteComponent,
  staticData: { suppressNotifications: true },
})

function RouteComponent() {
  const router = useRouter()
  const { mutateAsync: createProject } = useCreateProject(axiosRequest)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState<string>()
  const projectFormRef = useRef<ProjectFormRef>(null)

  const onBack = useCallback(() => {
    if (isSubmitting) return
    router.navigate({ to: '/projects', replace: true })
  }, [router, isSubmitting])

  const onSave = useCallback(
    async (data: ProjectFormData) => {
      setIsSubmitting(true)
      setSubmitError(undefined)

      try {
        await createProject(data)
        router.navigate({ to: '/projects', replace: true })
      } catch (error) {
        console.error(error)
        setSubmitError('Failed to create project')
      } finally {
        setIsSubmitting(false)
      }
    },
    [createProject, router],
  )

  return (
    <FullView
      leftAction={
        <ActionButton
          onClick={onBack}
          label="Discard"
          icon={<ChevronBackward className="size-6" />}
        />
      }
      centerContent={<div>New Project</div>}
      rightAction={
        <ActionButton
          primary
          onClick={() => {
            projectFormRef.current?.submit()
          }}
          label="Save project"
          disabled={isSubmitting}
          icon={<Check className="m-auto size-6" />}
        />
      }
    >
      <FullViewContent>
        <div className="h-full">
          <ProjectForm
            ref={projectFormRef}
            onSubmit={onSave}
            isSubmitting={isSubmitting}
            submitError={submitError}
          />
        </div>
      </FullViewContent>
    </FullView>
  )
}
